// src/pages/FollowingManager/FollowingManager.js
import React, { useEffect, useMemo, useState } from 'react';
import api from '../../../api';
import { formatKST } from '../../../util';
import styles from './FollowingManager.module.css';

const REGIONS = [
  '공식',
  '서울','경기','대구','대전','광주',
  '부산','인천','제주','전북','전남',
  '울산','경북','경남','강원',
  '충북','충남','세종'
];

const REGION_OPTIONS = ['전지역', ...REGIONS];
const STATUS_OPTIONS = ['전체', '팔로우 예정', '팔로우 중', '언팔함'];

function statusFromRow(row) {
  if (row.is_follow) return '팔로우 중';
  // is_follow = false
  return row.changed_at ? '언팔함' : '팔로우 예정';
}

function colorClassByStatus(status) {
  switch (status) {
    case '팔로우 중':  return styles.badgeOn;
    case '언팔함':     return styles.badgeOff;
    case '팔로우 예정':
    default:           return styles.badgePlan;
  }
}

const FollowingManager = ({ setAdViseStep, initialRegion }) => {
  // 필터 상태
  const safeInitial = useMemo(
    () => (initialRegion && REGION_OPTIONS.includes(initialRegion) ? initialRegion : '전지역'),
    [initialRegion]
  );
  const [selectedRegion, setSelectedRegion] = useState(safeInitial);
  const [selectedStatus, setSelectedStatus] = useState('전체');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);

  // 목록/로딩
  const [items, setItems] = useState([]); // [{username, region, is_follow, has_comment, created_at, changed_at}]
  const [loading, setLoading] = useState(false);
  const [hasFocus, setHasFocus] = useState(false);
  const [todayFollowCounts, setTodayFollowCounts] = useState({});

  const fetchTodayFollowCounts = async () => {
    try {
      const { data } = await api.get('/advise/admin/following/followed-today-counts');
      setTodayFollowCounts(data?.counts || {});
    } catch (e) {
      console.error('followed-today-counts error:', e);
    }
  };

  // 검색
  const [q, setQ] = useState('');
  const [suggests, setSuggests] = useState([]);
  const [suggestOpen, setSuggestOpen] = useState(false);
  const [searching, setSearching] = useState(false);

  const fetchList = async (opts = {}) => {
    const region  = opts.region ?? selectedRegion;
    const keyword = opts.q ?? '';

    setLoading(true);
    try {
      const { data } = await api.get('/advise/admin/following', {
        params: { region, status: '전체', q: keyword }
      });
      setItems(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error('following list error:', e);
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  // 초기 로드
  useEffect(() => {
    fetchList();
    fetchTodayFollowCounts();
  }, []);

  useEffect(() => {
    if (initialRegion && REGION_OPTIONS.includes(initialRegion)) {
      setSelectedRegion(initialRegion);
    }
  }, [initialRegion]);

  const counts = useMemo(() => {
    const list = items || [];
    let plan = 0, on = 0, off = 0, commented = 0;
    for (const row of list) {
      const st = statusFromRow(row);
      if (st === '팔로우 예정') plan++;
      else if (st === '팔로우 중') on++;
      else off++;
      if (row.has_comment) commented++;
    }
    return { total: list.length, plan, on, off, commented };
  }, [items]);

  const todaySelected = useMemo(() => {
    return selectedRegion === '전지역'
      ? (todayFollowCounts.__total ?? 0)
      : (todayFollowCounts[selectedRegion] ?? 0);
  }, [selectedRegion, todayFollowCounts]);

  // 검색어 변경 시 자동완성
  useEffect(() => {
    const v = q.trim();
    if (!v) {
      setSuggests([]);
      setSuggestOpen(false);
      return;
    }
    const run = async () => {
      try {
        const { data } = await api.get('/advise/admin/following/suggest', { params: { q: v } });
        setSuggests(Array.isArray(data) ? data.slice(0, 8) : []);
        setSuggestOpen(hasFocus);
      } catch (e) {
        console.error('suggest error:', e);
        setSuggests([]);
      }
    };
    const t = setTimeout(run, 150);
    return () => clearTimeout(t);
  }, [q, hasFocus]);

  useEffect(() => { setPage(1); }, [selectedRegion, selectedStatus, q]);

  function extractInstagramUsername(input) {
    const s = String(input || '').trim();
    if (!s) return '';

    // @username 형태
    if (s.startsWith('@')) return s.slice(1);

    // URL 형태 (https://, http://, www., m. 모두 케어)
    const ensureScheme = (t) => /^https?:\/\//i.test(t) ? t : `https://${t}`;
    try {
      const url = new URL(ensureScheme(s));
      const host = url.hostname.replace(/^www\./i, '').replace(/^m\./i, '');
      if (host === 'instagram.com') {
        const path = url.pathname.replace(/^\/+|\/+$/g, '');
        const segs = path.split('/').filter(Boolean);
        if (segs.length >= 1) {
          const candidate = segs[0];
          // 스토리/릴/포스트 등 예약 경로는 제외
          const reserved = new Set(['p','reel','tv','stories','explore','accounts','direct','challenge']);
          if (!reserved.has(candidate.toLowerCase())) return candidate;
        }
      }
    } catch (_) { /* noop */ }

    // www 없는 URL 형태 케어
    const m = s.match(/instagram\.com\/@?([A-Za-z0-9._]{1,30})/i);
    if (m) return m[1];

    // 마지막 fallback: 문자열에서 닉네임 패턴만 추출
    const m2 = s.match(/@?([A-Za-z0-9._]{1,30})/);
    return m2 ? m2[1] : s;
  }

  const filtered = useMemo(() => {
    return items
      .filter(it => {
        if (selectedRegion !== '전지역') {
          if ((it.region ?? null) !== selectedRegion) return false;
        }
        const st = statusFromRow(it);
        if (selectedStatus === '전체') return true;
        return st === selectedStatus;
      })
  }, [items, selectedRegion, selectedStatus]);

  // 총 페이지 계산 + 현재 페이지 클램프
  const total = filtered.length;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const currentPage = Math.min(page, totalPages);

  // 페이지에 해당하는 조각만 화면에 렌더
  const paged = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return filtered.slice(start, start + pageSize);
  }, [filtered, currentPage, pageSize]);

  // 검색 실행
  const doSearch = async (kw) => {
    const keyword = extractInstagramUsername(kw ?? q).trim().toLowerCase();
    if (!keyword) return;
    setSearching(true);
    setSuggestOpen(false);
    setSelectedRegion('전지역');
    setSelectedStatus('전체');
    await fetchList({ region: '전지역', status: '전체', q: keyword });
    setSearching(false);
  };

  // 자동완성 클릭
  const chooseSuggest = async (name) => {
    setQ(name);
    await doSearch(name);
  };

  // +추가
  const addUser = async () => {
    const name = extractInstagramUsername(q).trim().toLowerCase();
    if (!name) return alert('유저명을 입력해주세요.');
    try {
      const body = {
        username: name,
        region: selectedRegion === '전지역' ? null : selectedRegion
      };
      const { data } = await api.post('/advise/admin/following', body);
      // 서버에서 추가된 장부 1개를 반환 → 맨 위에 삽입
      setItems(prev => [data, ...prev]);
      setQ('');
      setSuggests([]);
      setSuggestOpen(false);
      fetchTodayFollowCounts();
    } catch (e) {
      const st = e?.response?.status;
      const data = e?.response?.data || {};
      if (st === 409) {
        if (data.code === 'DUP_IN_REGION') {
          const regionText = data.region ?? '지역무관';
          const statusText = data.status ?? '팔로우 예정';
          alert(`이미 등록된 유저입니다 (${regionText}/${statusText})`);
          return;
        }
        if (data.code === 'FOLLOWED_ELSEWHERE') {
          const list = (data.regions_following || []).join(', ');
          const ok = window.confirm(`이미 ${list} 계정에서 팔로잉 중입니다.\n이 계정으로도 추가하시겠습니까?`);
          if (ok) {
            try {
              const body2 = {
                username: name,
                region: selectedRegion === '전지역' ? null : selectedRegion,
                force: true
              };
              const { data: row2 } = await api.post('/advise/admin/following', body2);
              setItems(prev => [row2, ...prev]);
              setQ(''); setSuggests([]); setSuggestOpen(false);
              fetchTodayFollowCounts();
            } catch (e2) {
              console.error('add(force) error:', e2);
              alert('추가 실패');
            }
          }
          return;
        }
        // 구버전 호환
        const region = data.region ?? '지역무관';
        const status = data.status ?? '팔로우 예정';
        alert(`이미 등록된 유저입니다 (${region}/${status})`);
      } else {
        console.error('add user error:', e);
        alert('추가 실패');
      }
    }
  };

  // 상태 변경 (클릭 → 상태 순환)
  const rotateStatus = (cur) => {
    switch (cur) {
      case '팔로우 중':  return '언팔함';
      case '언팔함':     return '팔로우 예정';
      case '팔로우 예정':
      default:           return '팔로우 중';
    }
  };

  const onToggleStatus = async (row, currentStatus) => {
    const next = rotateStatus(currentStatus);
    // 가드: '팔로우 예정' → '팔로우 중' 전환 시 지역이 지정되어야 함
    if (currentStatus === '팔로우 예정' && next === '팔로우 중' && (row.region == null)) {
      alert('지역이 지정되어야 팔로우 중으로 변경할 수 있어요.');
      return;
    }
    try {
      const { data: updated } = await api.patch(
        `/advise/admin/following/${encodeURIComponent(row.username)}/status`,
        { status: next, region: row.region ?? null }
      );
      // 반환받은 row로 교체
      setItems(prev => prev.map(it => (it.username === row.username && (it.region ?? null) === (row.region ?? null)) ? updated : it));
      fetchTodayFollowCounts();
    } catch (e) {
      console.error('toggle status error:', e);
      alert('상태 변경 실패');
    }
  };

  // region 변경 (is_follow=false 때만)
  const onChangeRegion = async (row, newRegion) => {
    // 프론트 가드: 팔로우 예정 상태에서만 변경 허용
    if (statusFromRow(row) !== '팔로우 예정') {
      alert('팔로우 예정 상태에서만 지역을 변경할 수 있습니다.');
      return;
    }
    try {
      const { data: updated } = await api.patch(
        `/advise/admin/following/${encodeURIComponent(row.username)}/region`,
        {
          from_region: row.region ?? null,
          region: (newRegion === '지역무관' ? null : newRegion)
        }
      );
      setItems(prev => prev.map(it => (it.username === row.username && (it.region ?? null) === (row.region ?? null)) ? updated : it));
      fetchTodayFollowCounts();
    } catch (e) {
      if (e?.response?.status === 400) {
        alert('팔로우 예정 상태에서만 지역을 변경할 수 있습니다.');
      } else if (e?.response?.status === 409) {
        alert('이미 해당 지역으로 등록되어 있습니다.');
      } else {
        console.error('change region error:', e);
        alert('지역 변경 실패');
      }
    }
  };

  // 삭제
  const onDelete = async (row) => {
    const ok = window.confirm(`정말 삭제할까요?\n@${row.username} (${row.region ?? '지역무관'})`);
    if (!ok) return;
    try {
      const params = new URLSearchParams();
      if (row.region != null) params.set('region', row.region);
      await api.delete(`/advise/admin/following/${encodeURIComponent(row.username)}?${params.toString()}`);
      setItems(prev => prev.filter(it =>
        !(it.username === row.username && (it.region ?? null) === (row.region ?? null))
      ));
      fetchTodayFollowCounts();
    } catch (e) {
      console.error('delete error:', e);
      alert('삭제 실패');
    }
  };

  useEffect(() => {
    const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
    if (page > totalPages) setPage(totalPages);
  }, [filtered.length, pageSize]); 

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <img
          className={styles.backbuttonIcon}
          alt=""
          src="/images/back_android.svg"
          onClick={() => setAdViseStep(0)}
        />
        <h2>팔로잉 관리</h2>
        <div className={styles.headerRight}>
          <button className={styles.btn} onClick={() => fetchList()} disabled={loading}>새로고침</button>
        </div>
      </div>

      <div className={styles.stats}>
        <div>선택 지역: <b>{selectedRegion}</b></div>
        <div>
          오늘 팔로우{" "}
          <b className={(selectedRegion !== '전지역' && todaySelected >= 10) ? styles.countHot : undefined}>
            {todaySelected}
          </b>
        </div>
        <div>팔로우 예정 <b>{counts.plan}</b></div>
        <div>팔로우 중 <b>{counts.on}</b></div>
      </div>

      {/* 필터 바 */}
      <div className={styles.filters}>
        <div className={styles.chips}>
          {REGION_OPTIONS.map(r => {
            const count = r === '전지역'
              ? (todayFollowCounts.__total ?? 0)         // ✅ 전지역 = 합계
              : (todayFollowCounts[r] ?? 0);             // ✅ 해당 지역
            const hot = (r !== '전지역') && count >= 10;
            return (
              <button
                key={r}
                className={selectedRegion === r ? styles.chipOn : styles.chipOff}
                onClick={() => {
                  setSelectedRegion(r);
                  // 검색 모드 끊기(있으면)
                  if (searching) setSearching(false);
                  // 선택된 상태값 그대로, 지역만 바꿔서 즉시 재조회
                  fetchList({ region: r, q: '' });
                }}
              >
                {r}
                <span className={hot ? styles.countHot : styles.countNorm}>
                  ({count})
                </span>
              </button>
            );
          })}
        </div>
      </div>
      <div className={styles.filters}>
        <div className={styles.chipsRight}>
          {STATUS_OPTIONS.map(s => {
            const n = s === '전체'
              ? counts.total
              : s === '팔로우 예정'
                ? counts.plan
                : s === '팔로우 중'
                  ? counts.on
                  : counts.off;
            return (
              <button
                key={s}
                className={selectedStatus === s ? styles.chipOn : styles.chipOff}
                onClick={() => {
                  setSelectedStatus(s);
                  if (searching) setSearching(false);
                }}
              >
                {s} ({n})
              </button>
            );
          })}
        </div>
      </div>

      {/* 검색/추가 바 */}
      <div className={styles.searchBar}>
        <div className={styles.searchBox}>
          <input
            className={styles.searchInput}
            value={q}
            onChange={(e)=>setQ(e.target.value)}
            placeholder="username 검색…"
            onFocus={() => { setHasFocus(true); setSuggestOpen(true); }}
            onBlur={() => { setHasFocus(false); setTimeout(() => setSuggestOpen(false), 100); }}
            onPaste={(e) => {
              const text = (e.clipboardData || window.clipboardData).getData('text');
              const uname = extractInstagramUsername(text);
              if (uname && uname !== text.trim()) {
                e.preventDefault();       // 원문 붙여넣기 막고
                setQ(uname);              // 닉네임만 세팅
              }
            }}
          />
          <button className={styles.btnLight} onClick={() => doSearch()}>검색</button>
          <button className={styles.btnAdd} onClick={() => addUser()}>+ 추가</button>

          {suggestOpen && hasFocus && suggests.length > 0 && (
            <div className={styles.suggest}>
              {suggests.map(name => (
                <div
                  key={name}
                  className={styles.suggestItem}
                  onMouseDown={(e)=>e.preventDefault()}
                  onClick={() => chooseSuggest(name)}
                >
                  {name}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* 리스트 */}
      <div className={styles.list}>
        <div className={styles.rowHead}>
          <div className={styles.colUser0}>닉네임</div>
          <div className={styles.colRegion}>계정</div>
          <div className={styles.colFlag}>선댓글</div>
          <div className={styles.colDate}>등록일</div>
          <div className={styles.colStatus}>상태</div>
          <div className={styles.colActions}>삭제</div>
        </div>

        {loading ? (
          <div className={styles.loading}>불러오는 중…</div>
        ) : filtered.length === 0 ? (
          <div className={styles.empty}>데이터가 없습니다.</div>
        ) : (
          paged.map(row => {
            const status = statusFromRow(row);
            return (
              <div key={`${row.username}__${row.region ?? 'NULL'}`} className={styles.row}>
                <div className={styles.colUser} onClick={()=>window.open(`https://instagram.com/${row.username}`, '_blank')}>{row.username}</div>

                <div className={styles.colRegion}>
                  {statusFromRow(row) === '팔로우 예정' ? (
                    <select
                      className={styles.regionSelect}
                      value={row.region ?? '지역무관'}
                      onChange={(e)=>onChangeRegion(row, e.target.value)}
                    >
                      <option value="지역무관">지역무관</option>
                      {REGIONS.map(r => <option key={r} value={r}>{r}</option>)}
                    </select>
                  ) : (
                    <span className={styles.regionText}>
                      {row.region ?? '지역무관'}
                    </span>
                  )}
                </div>

                <div className={styles.colFlag}>{row.has_comment ? 'O' : '-'}</div>
                <div className={styles.colDate}>{formatKST(row.created_at)}</div>

                <div className={styles.colStatus}>
                  <button
                    className={`${styles.statusBadge} ${colorClassByStatus(status)}`}
                    onClick={() => onToggleStatus(row, status)}
                    title="클릭하여 상태 변경"
                  >
                    {status}
                  </button>
                </div>

                <div className={styles.colActions}>
                  <img className={styles.delXBtn} onClick={() => onDelete(row)} src='/images/close_line.svg'/>
                </div>
              </div>
            );
          })
        )}
      </div>

      <div className={styles.pager}>
        <div className={styles.pagerInfo}>
          총 {total.toLocaleString()}건 · 페이지 {currentPage}/{totalPages}
        </div>
        <div className={styles.pagerCtrls}>
          <select
            className={styles.pageSize}
            value={pageSize}
            onChange={e => { setPageSize(Number(e.target.value)); setPage(1); }}
          >
            <option value={25}>25개</option>
            <option value={50}>50개</option>
            <option value={100}>100개</option>
            <option value={200}>200개</option>
          </select>
          <button disabled={currentPage === 1} onClick={() => setPage(1)}>처음</button>
          <button disabled={currentPage === 1} onClick={() => setPage(p => p - 1)}>이전</button>
          <button disabled={currentPage === totalPages} onClick={() => setPage(p => p + 1)}>다음</button>
          <button disabled={currentPage === totalPages} onClick={() => setPage(totalPages)}>마지막</button>
        </div>
      </div>
    </div>
  );
};

export default FollowingManager;
